import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// The session exchange MUST use window.location.origin so the call goes to the same Emergent
// environment that created the session. Using REACT_APP_BACKEND_URL (preview URL) for the
// exchange when the user is on a custom domain (app.wordaliveglobal.org) causes a 404 from
// Emergent Auth because sessions are environment-scoped.

// Module-level flag — survives React StrictMode's unmount/remount cycle.
// useRef(false) resets on each new component instance (remount), so the second
// StrictMode mount would re-run processAuth with the same session_id, causing
// Emergent Auth to reject it (one-time token) and show "Login failed".
let hasProcessedAuth = false;

/** Attempt POST /api/auth/session with retries for transient failures (502/503/520). */
async function exchangeSession(url, sessionId, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await axios.post(url, { session_id: sessionId }, { timeout: 12000 });
    } catch (err) {
      const status = err.response?.status;
      const isTransient = !status || status === 502 || status === 503 || status >= 520;
      if (isTransient && attempt < retries) {
        console.log(`Auth exchange attempt ${attempt + 1} failed (${status || "network"}), retrying...`);
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (hasProcessedAuth) return;
    hasProcessedAuth = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      console.log("AuthCallback mount – current hash:", hash);

      const match = hash.match(/session_id=([^&]+)/);
      if (!match) {
        console.log("No session_id found in hash — redirecting home");
        navigate("/", { replace: true });
        return;
      }

      const sessionId = match[1];
      console.log("session_id found, length:", sessionId.length);

      // Use current origin so session exchange hits the same Emergent environment
      // that created the session — fixes custom domain (app.wordaliveglobal.org) auth
      const callbackApi = window.location.origin + "/api";

      // Read redirect targets BEFORE the async call (they'll be cleaned up after)
      const pendingCollective = localStorage.getItem("wagc_pending_collective");
      let pendingRedirect = localStorage.getItem("wagc_auth_redirect");

      // Defensive: sanitize stale cross-domain redirect values
      if (pendingRedirect) {
        const isAbsoluteUrl = /^https?:\/\//i.test(pendingRedirect);
        if (isAbsoluteUrl) {
          if (pendingRedirect.startsWith(window.location.origin)) {
            pendingRedirect = pendingRedirect.replace(window.location.origin, "");
          } else {
            pendingRedirect = null;
          }
        }
      }

      // Clean up localStorage regardless of outcome
      localStorage.removeItem("wagc_pending_collective");
      localStorage.removeItem("wagc_auth_redirect");

      // Compute fallback destination (used on both success and failure)
      const fallbackDest = pendingRedirect && pendingRedirect !== "/collectives"
        ? pendingRedirect
        : "/";

      try {
        const res = await exchangeSession(`${callbackApi}/auth/session`, sessionId);

        // Store session token for Bearer auth
        const token = res.data.token || res.data.session_token;
        if (token) {
          localStorage.setItem("auth_token", token);
          localStorage.setItem("wagc_session_token", token);
        }

        // Determine post-login destination.
        let destination;
        if (pendingCollective) {
          destination = `/collectives/${pendingCollective}/room`;
        } else if (res.data?.role === "apostle") {
          destination = "/den";
        } else if (res.data?.role === "admin") {
          destination = "/dashboard";
        } else if (pendingRedirect && pendingRedirect !== "/collectives") {
          destination = pendingRedirect;
        } else if (["it_admin", "staff", "bookstore_staff", "community_room_staff",
                     "administrative_staff", "links_staff"].includes(res.data?.role)) {
          destination = "/wa-ops-center";
        } else {
          // Regular members land on Collectives — the heart of community life
          destination = "/collectives";
        }

        console.log("AuthCallback final navigation:", destination);
        await refresh();
        navigate(destination, { replace: true, state: { user: res.data } });
      } catch (err) {
        console.error("Auth exchange failed:", err.response?.status, err.response?.data, err.message);
        const status = err.response?.status;
        const detail = err.response?.data?.detail;

        let msg;
        if (!status) {
          msg = "Could not reach the server — please check your connection and try again.";
        } else if (status === 401) {
          msg = typeof detail === "string" ? detail : "Session expired — please sign in again.";
        } else if (status === 429) {
          msg = "Too many login attempts — please wait a moment.";
        } else {
          msg = typeof detail === "string" ? detail : "Login failed — please try again.";
        }

        toast.error(msg);
        navigate(fallbackDest, { replace: true });
      }
    };

    processAuth().finally(() => { hasProcessedAuth = false; });
  }, [navigate, refresh]);

  return null;
}
